import { useEffect, useRef, useState } from "react";

const parseAtributos = (raw) => {
  if (!raw) {
    return {};
  }

  if (typeof raw === "object") {
    return raw;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    console.error("Atributos SSO inválidos:", error);
    return {};
  }
};

const findAppData = (atributos, empresa, appName) => {
  const empresaKey = String(empresa ?? "");

  if (empresaKey && atributos?.[empresaKey]?.[appName]) {
    return atributos[empresaKey][appName];
  }

  for (const key of Object.keys(atributos || {})) {
    if (atributos[key]?.[appName]) {
      return atributos[key][appName];
    }
  }

  return null;
};

const normalizeRoles = (roles) => {
  if (!Array.isArray(roles)) {
    return [];
  }

  return roles
    .map((role) => {
      if (typeof role === "string") {
        return role;
      }

      return role?.authority || role?.nombre || "";
    })
    .filter(Boolean);
};

const PLANIFICADOR_ROLE_BY_PROFILE = {
  ADMIN: "ROLE_ADMIN",
  ADMIN_PLANIFICADOR: "ROLE_ADMIN_PLANIFICADOR",
  COORDINADOR_VENDEDOR: "ROLE_COORDINADOR_VENDEDOR",
  VENDEDOR_PLANIFICADOR: "ROLE_VENDEDOR_PLANIFICADOR",
  COORDINADOR_MAYORISTA: "ROLE_COORDINADOR_MAYORISTA",
  COORDINADOR_SIN_CANAL: "ROLE_COORDINADOR_SIN_CANAL",
  VENDEDOR_RETAIL: "ROLE_VENDEDOR_RETAIL",
  VENDEDOR_MAYORISTA: "ROLE_VENDEDOR_MAYORISTA",
};

const getPlanificadorRole = (perfil) =>
  PLANIFICADOR_ROLE_BY_PROFILE[String(perfil || "").toUpperCase()] || "";

const getSsoAppNameByEmpresa = (empresa) =>
  Number(empresa) === 6 ? "PLANIFICADORSACK" : "PLANIFICADOR";

export const useSSOAuth = ({
  appName = "",
  ssoUrl = process.env.REACT_APP_SSO_USER_URL ||
    "https://sso.kesp.cl/sso/rest/user",
  loginUrl = process.env.REACT_APP_SSO_LOGIN_URL ||
    "https://sso.kesp.cl/sso/login.htm",
  enabled = true,
  onSuccess,
}) => {
  const [isLoggedIn, setIsLoggedIn] = useState(
    sessionStorage.getItem("isLoggedIn") === "true",
  );
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const onSuccessRef = useRef(onSuccess);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    const checkSession = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(ssoUrl, {
          method: "GET",
          credentials: "include",
          signal: controller.signal,
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`SSO respondió HTTP ${response.status}`);
        }

        const userData = await response.json();

        const resolvedAppName =
          appName || getSsoAppNameByEmpresa(userData.empresa);
        const apps = Array.isArray(userData.apps) ? userData.apps : [];

        if (!apps.includes(resolvedAppName)) {
          throw new Error(`Usuario sin acceso a ${resolvedAppName}`);
        }

        const atributos = parseAtributos(userData.atributos);

        const appData = findAppData(atributos, userData.empresa, resolvedAppName);

        if (!appData) {
          throw new Error(
            `No existe configuración ${resolvedAppName} para la empresa activa`,
          );
        }

        const ssoRoles = normalizeRoles(userData.roles);
        const perfil = String(appData.perfil ?? "").toUpperCase();
        const planificadorRole = getPlanificadorRole(perfil);

        if (!planificadorRole) {
          throw new Error(
            `Perfil ${perfil || "sin perfil"} no reconocido para ${resolvedAppName}`,
          );
        }

        const roles = [planificadorRole];

        const propiedades = parseAtributos(userData.propiedades);
        const appProperties = propiedades?.[resolvedAppName] || {};

        const permisos =
          typeof appData.permisos === "string"
            ? appData.permisos
            : JSON.stringify(appData.permisos ?? []);

        sessionStorage.removeItem("authUsername");
        sessionStorage.removeItem("authPassword");

        sessionStorage.setItem("isLoggedIn", "true");
        sessionStorage.setItem("apellido", userData.apellido || "");
        sessionStorage.setItem("roles", JSON.stringify(roles));
        sessionStorage.setItem("ssoRoles", JSON.stringify(ssoRoles));
        sessionStorage.setItem("usuario_id", String(userData.id ?? ""));
        sessionStorage.setItem("username", userData.usuario || "");
        sessionStorage.setItem("user", userData.usuario || "");
        sessionStorage.setItem("nombre", userData.nombre || "");
        sessionStorage.setItem("empresaActiva", String(userData.empresa ?? ""));
        sessionStorage.setItem("perfil", perfil);
        sessionStorage.setItem("ssoAppName", resolvedAppName);
        sessionStorage.setItem("permisos", permisos);
        sessionStorage.setItem(
          "propiedadesPlanificador",
          JSON.stringify(appProperties),
        );
        sessionStorage.setItem("authority", planificadorRole);

        if (!active) {
          return;
        }

        setIsLoggedIn(true);
        onSuccessRef.current?.(userData);
      } catch (authError) {
        if (authError.name === "AbortError") {
          return;
        }

        console.error("Error validando sesión SSO:", authError);

        sessionStorage.removeItem("isLoggedIn");

        if (active) {
          setError(authError);
          setIsLoggedIn(false);
        }

        const redirectUrl = `${loginUrl}?redirect=${encodeURIComponent(
          window.location.href,
        )}`;

        window.location.replace(redirectUrl);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    checkSession();

    return () => {
      active = false;
      controller.abort();
    };
  }, [appName, ssoUrl, loginUrl, enabled]);

  return {
    isLoggedIn,
    loading,
    error,
  };
};
